/**
 * Client for the Square Connection admin endpoints (feat-010-square-connect-admin).
 *
 * Contracts:
 *  - Spec:  worktrees/feat-010-square-connect-admin/spec.md
 *  - ADR 0001 (web OAuth flow) — initiate body, callback flash params
 *  - ADR 0002 (location-mapping schema) — connection-state response shape
 *
 * Fake mode: when `config.environment === 'fake'` (npm run start:fake), every
 * call returns canned data so the card renders without a backend. A
 * `?square_state=<state>` URL param overrides the fake connection state so
 * QA can flip between the five branches.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

// ---------- Domain types (mirror the spec) ----------

export type TenantConnectionStatus = 'not_connected' | 'active' | 'needs_reauth';

export interface TenantConnection {
  status: TenantConnectionStatus;
  merchant_id?: string;
  connected_at?: string; // ISO
  /**
   * feat-039 B6a: whether the merchant has granted the Square `TIMECARDS_READ`
   * scope needed for Labor/coverage data. `false` on a merchant connected
   * before the scope was added → drives the "Re-authorize for Labor data" CTA.
   * Optional so pre-B6a backends (which omit it) don't surface the CTA.
   */
  labor_authorized?: boolean;
}

// Per ADR 0002 D4 (hard-delete on disconnect) and D8 (response shape), the
// backend only ever returns "unmapped" or "mapped". Absence of a mapping row
// presents as "unmapped" — there is no "disconnected" status on the wire.
export type LocationMappingStatus = 'unmapped' | 'mapped';

export interface LocationMapping {
  status: LocationMappingStatus;
  square_location_id?: string;
  square_location_name?: string;
  mapped_at?: string; // ISO
}

export interface ConnectionState {
  tenant_connection: TenantConnection;
  location_mapping: LocationMapping;
  /** Most-recent POS-table row for this Bespoke location, or null. */
  last_transaction_at: string | null;
}

export interface SquareLocation {
  id: string;
  name: string;
  address?: string;
  /** If set, this Square location is already mapped to another Bespoke location in the tenant. */
  currently_mapped_to_bespoke_location_id?: string;
  currently_mapped_to_bespoke_location_name?: string;
}

export interface SquareLocationsResponse {
  locations: SquareLocation[];
}

export type PaymentMethod = 'Card' | 'Cash' | 'Gift' | 'Other';

export interface SquareTransactionRow {
  id: string;
  occurred_at: string;       // ISO — used for "{time ago}"
  total: number;             // dollars (DECIMAL from backend may arrive as string — coerce here)
  item_count: number;
  payment_method: PaymentMethod | null;
  square_employee: string | null;
  first_item_name: string | null;
}

export interface SquareTransactionsResponse {
  rows: SquareTransactionRow[];
}

// Per-order receipt payload for the detail modal (feat-011). DECIMAL columns
// arrive as strings from MySQL — coerced to numbers at the service edge to
// match the existing SquareTransactionRow.total precedent.
export interface SquareLineItem {
  name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

export interface SquareTransactionPayment {
  method: PaymentMethod;
  last4: string | null;
}

export interface SquareTransactionDetail {
  id: string;
  vendor_order_id: string;
  occurred_at: string; // ISO-8601 UTC
  location_name: string;
  employee_name: string | null;
  total: number;
  subtotal: number;
  tax_total: number;
  tip_total: number;
  discount_total: number;
  line_items: SquareLineItem[];
  payment: SquareTransactionPayment;
}

export interface OAuthInitiateResponse {
  redirect_url: string;
}

export interface MappingConflictBody {
  conflict_bespoke_location_id: string;
  conflict_bespoke_location_name: string;
}

export class MappingConflictError extends Error {
  conflict: MappingConflictBody;
  constructor(conflict: MappingConflictBody) {
    super(`Square location already mapped to ${conflict.conflict_bespoke_location_name}`);
    this.name = 'MappingConflictError';
    this.conflict = conflict;
  }
}

export interface DisconnectTenantResponse {
  mappings_disconnected: number;
}

/**
 * Tenant-wide rollup that powers the green "Healthy" pill on the locations
 * admin list (/admin/locations). Healthy = the tenant has an active Square
 * merchant AND the location has a mapping row. `not_connected`,
 * `disconnected`, and `needs_reauth` all return an empty list.
 */
export interface TenantConnectionSummary {
  healthy_location_ids: string[];
}

// ---------- HTTP plumbing ----------

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

function backendUrl(path: string): string {
  const protocol = config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
};

async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) {
    init.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
  }
  const resp = await authedApiFetch(backendUrl(path), init);
  if (resp.status === 409) {
    const text = await resp.text();
    try {
      const body = JSON.parse(text) as MappingConflictBody;
      if (body && body.conflict_bespoke_location_id) {
        throw new MappingConflictError(body);
      }
    } catch (e) {
      if (e instanceof MappingConflictError) throw e;
    }
    throw new Error(`API ${path} 409: ${text}`);
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} ${resp.status}: ${text}`);
  }
  if (resp.status === 204) return undefined as unknown as T;
  return resp.json();
}

// ---------- Fake mode ----------
// Lets npm run start:fake render every branch without a backend. The state
// branch can be overridden with `?square_state=<state>`.

function fakeStateOverride(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('square_state');
  } catch {
    return null;
  }
}

const FAKE_TXNS: SquareTransactionRow[] = [
  { id: 'f-1', occurred_at: new Date(Date.now() - 12 * 60_000).toISOString(),  total: 42.18, item_count: 3, payment_method: 'Card',  square_employee: 'Jane K.', first_item_name: 'Steak Shawarma' },
  { id: 'f-2', occurred_at: new Date(Date.now() - 34 * 60_000).toISOString(),  total: 18.75, item_count: 2, payment_method: 'Cash',  square_employee: 'Alex P.', first_item_name: 'Döner Combo' },
  { id: 'f-3', occurred_at: new Date(Date.now() - 67 * 60_000).toISOString(),  total: 7.50,  item_count: 1, payment_method: 'Card',  square_employee: null,      first_item_name: 'Dubai Chocolate Bar' },
  { id: 'f-4', occurred_at: new Date(Date.now() - 92 * 60_000).toISOString(),  total: 24.00, item_count: 2, payment_method: 'Gift',  square_employee: 'Maria S.', first_item_name: 'Falafel Wrap' },
  { id: 'f-5', occurred_at: new Date(Date.now() - 130 * 60_000).toISOString(), total: 56.40, item_count: 4, payment_method: 'Card',  square_employee: 'Jane K.', first_item_name: 'Lamb Plate' },
];

function fakeConnectionState(): ConnectionState {
  const override = fakeStateOverride();
  const now = new Date().toISOString();
  if (override === 'not_connected') {
    return {
      tenant_connection: { status: 'not_connected' },
      location_mapping: { status: 'unmapped' },
      last_transaction_at: null,
    };
  }
  if (override === 'unmapped') {
    return {
      tenant_connection: { status: 'active', merchant_id: 'M-FAKE', connected_at: now },
      location_mapping: { status: 'unmapped' },
      last_transaction_at: null,
    };
  }
  if (override === 'needs_reauth') {
    return {
      tenant_connection: { status: 'needs_reauth', merchant_id: 'M-FAKE', connected_at: now },
      location_mapping: { status: 'mapped', square_location_id: 'sq-loc-1', square_location_name: 'Main Street', mapped_at: now },
      last_transaction_at: new Date(Date.now() - 2 * 60 * 60_000).toISOString(),
    };
  }
  if (override === 'quiet') {
    return {
      tenant_connection: { status: 'active', merchant_id: 'M-FAKE', connected_at: now },
      location_mapping: { status: 'mapped', square_location_id: 'sq-loc-1', square_location_name: 'Main Street', mapped_at: now },
      last_transaction_at: new Date(Date.now() - 3 * 24 * 60 * 60_000).toISOString(),
    };
  }
  if (override === 'waiting') {
    return {
      tenant_connection: { status: 'active', merchant_id: 'M-FAKE', connected_at: now, labor_authorized: true },
      location_mapping: { status: 'mapped', square_location_id: 'sq-loc-1', square_location_name: 'Main Street', mapped_at: now },
      last_transaction_at: null,
    };
  }
  if (override === 'labor_reauth') {
    // feat-039 B6a: healthy mapped connection but TIMECARDS_READ not yet granted
    // (pre-existing merchant) → drives the "Re-authorize for Labor data" CTA.
    return {
      tenant_connection: { status: 'active', merchant_id: 'M-FAKE', connected_at: now, labor_authorized: false },
      location_mapping: { status: 'mapped', square_location_id: 'sq-loc-1', square_location_name: 'Main Street', mapped_at: now },
      last_transaction_at: new Date(Date.now() - 12 * 60_000).toISOString(),
    };
  }
  // Default: healthy mapped
  return {
    tenant_connection: { status: 'active', merchant_id: 'M-FAKE', connected_at: now, labor_authorized: true },
    location_mapping: { status: 'mapped', square_location_id: 'sq-loc-1', square_location_name: 'Main Street', mapped_at: now },
    last_transaction_at: new Date(Date.now() - 12 * 60_000).toISOString(),
  };
}

const FAKE_SQUARE_LOCATIONS: SquareLocation[] = [
  { id: 'sq-loc-1', name: 'Main Street',  address: '123 Main St' },
  { id: 'sq-loc-2', name: 'Downtown',     address: '500 5th Ave' },
  { id: 'sq-loc-3', name: 'Airport',      address: 'Terminal B', currently_mapped_to_bespoke_location_id: 'other-loc', currently_mapped_to_bespoke_location_name: 'North Concourse' },
];

const isFake = (): boolean => config.environment === 'fake';

// ---------- Public API ----------

export async function getConnectionState(bespokeLocationId: string): Promise<ConnectionState> {
  if (isFake()) return fakeConnectionState();
  return api<ConnectionState>(`/api/admin/locations/${encodeURIComponent(bespokeLocationId)}/square-connection`);
}

export async function initiateOAuth(
  bespokeLocationId: string,
  returnTo?: string,
): Promise<OAuthInitiateResponse> {
  const return_to = returnTo ?? (typeof window !== 'undefined' ? window.location.pathname : '/admin');
  if (isFake()) {
    // Simulate the redirect dance without leaving the page in fake mode.
    // Use the ADR 0001 D7 flash convention so LocationAdminView reads it.
    return {
      redirect_url: `${return_to}?square_oauth=success&merchant_id=fake-merchant&square_state=unmapped`,
    };
  }
  return api<OAuthInitiateResponse>('/api/admin/square/oauth/initiate', {
    method: 'POST',
    body: { bespoke_location_id: bespokeLocationId, return_to },
  });
}

export async function listSquareLocations(tenantId: string): Promise<SquareLocationsResponse> {
  if (isFake()) return { locations: FAKE_SQUARE_LOCATIONS };
  return api<SquareLocationsResponse>(`/api/admin/tenants/${encodeURIComponent(tenantId)}/square/locations`);
}

export async function getTenantConnectionSummary(
  tenantId: string,
): Promise<TenantConnectionSummary> {
  if (isFake()) {
    // Fake mode: drive from the same ?square_state override as fakeConnectionState so
    // QA can see the pill toggle. Only the default "healthy mapped" branch marks the
    // sole fake location id as Healthy.
    const override = fakeStateOverride();
    const isHealthy = override === null || override === 'quiet' || override === 'waiting';
    return { healthy_location_ids: isHealthy ? ['fake-loc-1'] : [] };
  }
  return api<TenantConnectionSummary>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/square-connection-summary`,
  );
}

export async function setLocationMapping(
  bespokeLocationId: string,
  squareLocationId: string,
): Promise<LocationMapping> {
  if (isFake()) {
    const loc = FAKE_SQUARE_LOCATIONS.find((l) => l.id === squareLocationId);
    if (loc?.currently_mapped_to_bespoke_location_id) {
      throw new MappingConflictError({
        conflict_bespoke_location_id: loc.currently_mapped_to_bespoke_location_id,
        conflict_bespoke_location_name: loc.currently_mapped_to_bespoke_location_name ?? 'another location',
      });
    }
    return {
      status: 'mapped',
      square_location_id: squareLocationId,
      square_location_name: loc?.name ?? squareLocationId,
      mapped_at: new Date().toISOString(),
    };
  }
  return api<LocationMapping>(
    `/api/admin/locations/${encodeURIComponent(bespokeLocationId)}/square-mapping`,
    { method: 'PUT', body: { square_location_id: squareLocationId } },
  );
}

export async function removeLocationMapping(bespokeLocationId: string): Promise<void> {
  if (isFake()) return;
  await api<void>(
    `/api/admin/locations/${encodeURIComponent(bespokeLocationId)}/square-mapping`,
    { method: 'DELETE' },
  );
}

export async function disconnectTenant(tenantId: string): Promise<DisconnectTenantResponse> {
  if (isFake()) return { mappings_disconnected: 1 };
  return api<DisconnectTenantResponse>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/square-connection`,
    { method: 'DELETE' },
  );
}

export async function listRecentTransactions(
  bespokeLocationId: string,
  limit: number = 10,
): Promise<SquareTransactionsResponse> {
  if (isFake()) {
    const state = fakeConnectionState();
    if (state.location_mapping.status !== 'mapped' || state.last_transaction_at === null) {
      return { rows: [] };
    }
    return { rows: FAKE_TXNS.slice(0, Math.min(limit, FAKE_TXNS.length)) };
  }
  // Backend hard-caps limit ≤ 50; pre-cap on the client so we never ask for more.
  const safeLimit = Math.max(1, Math.min(limit, 50));
  const raw = await api<{ rows: Array<Record<string, unknown>> }>(
    `/api/admin/locations/${encodeURIComponent(bespokeLocationId)}/square-transactions?limit=${safeLimit}`,
  );
  // DECIMAL columns arrive as strings from MySQL — coerce at the edge per
  // interviewer-frontend/CLAUDE.md.
  const rows: SquareTransactionRow[] = (raw.rows ?? []).map((r) => ({
    id: String(r.id ?? ''),
    occurred_at: String(r.occurred_at ?? ''),
    total: typeof r.total === 'string' ? parseFloat(r.total) : Number(r.total ?? 0),
    item_count: Number(r.item_count ?? 0),
    payment_method: (r.payment_method as PaymentMethod) ?? null,
    square_employee: (r.square_employee as string) ?? null,
    first_item_name: (r.first_item_name as string) ?? null,
  }));
  return { rows };
}

// ---------- Fake transaction detail (feat-011) ----------

const FAKE_TXN_DETAILS: Record<string, SquareTransactionDetail> = {
  'f-1': {
    id: 'f-1',
    vendor_order_id: 'sq-order-f-1',
    occurred_at: new Date(Date.now() - 12 * 60_000).toISOString(),
    location_name: 'Main Street',
    employee_name: 'Jane K.',
    total: 42.18,
    subtotal: 38.50,
    tax_total: 3.68,
    tip_total: 0,
    discount_total: 0,
    line_items: [
      { name: 'Steak Shawarma', quantity: 1, unit_price: 18.50, line_total: 18.50 },
      { name: 'Lamb & Rice Plate (extra sauce)', quantity: 1, unit_price: 14.00, line_total: 14.00 },
      { name: 'Baklava', quantity: 2, unit_price: 3.00, line_total: 6.00 },
    ],
    payment: { method: 'Card', last4: '4242' },
  },
  'f-2': {
    id: 'f-2',
    vendor_order_id: 'sq-order-f-2',
    occurred_at: new Date(Date.now() - 34 * 60_000).toISOString(),
    location_name: 'Main Street',
    employee_name: 'Alex P.',
    total: 18.75,
    subtotal: 17.25,
    tax_total: 1.50,
    tip_total: 0,
    discount_total: 0,
    line_items: [
      { name: 'Döner Combo', quantity: 1, unit_price: 12.25, line_total: 12.25 },
      { name: 'Soda', quantity: 1, unit_price: 5.00, line_total: 5.00 },
    ],
    payment: { method: 'Cash', last4: null },
  },
  'f-3': {
    id: 'f-3',
    vendor_order_id: 'sq-order-f-3',
    occurred_at: new Date(Date.now() - 67 * 60_000).toISOString(),
    location_name: 'Main Street',
    employee_name: null,
    total: 7.50,
    subtotal: 6.50,
    tax_total: 0.50,
    tip_total: 1.00,
    discount_total: 0,
    line_items: [
      { name: 'Dubai Chocolate Bar', quantity: 1, unit_price: 6.50, line_total: 6.50 },
    ],
    payment: { method: 'Card', last4: '1234' },
  },
  'f-4': {
    id: 'f-4',
    vendor_order_id: 'sq-order-f-4',
    occurred_at: new Date(Date.now() - 92 * 60_000).toISOString(),
    location_name: 'Main Street',
    employee_name: 'Maria S.',
    total: 24.00,
    subtotal: 22.50,
    tax_total: 1.50,
    tip_total: 0,
    discount_total: 2.00,
    line_items: [
      { name: 'Falafel Wrap', quantity: 2, unit_price: 11.00, line_total: 22.00 },
      { name: 'Hummus Side', quantity: 1, unit_price: 3.50, line_total: 3.50 },
    ],
    payment: { method: 'Gift', last4: null },
  },
  'f-5': {
    id: 'f-5',
    vendor_order_id: 'sq-order-f-5',
    occurred_at: new Date(Date.now() - 130 * 60_000).toISOString(),
    location_name: 'Main Street',
    employee_name: 'Jane K.',
    total: 56.40,
    subtotal: 51.00,
    tax_total: 5.40,
    tip_total: 0,
    discount_total: 0,
    line_items: [
      { name: 'Lamb Plate', quantity: 2, unit_price: 22.00, line_total: 44.00 },
      { name: 'Sparkling Water', quantity: 1, unit_price: 4.00, line_total: 4.00 },
      { name: 'Baklava', quantity: 1, unit_price: 3.00, line_total: 3.00 },
    ],
    payment: { method: 'Card', last4: '9876' },
  },
};

export async function getTransactionDetail(
  bespokeLocationId: string,
  posOrderId: string,
): Promise<SquareTransactionDetail> {
  if (isFake()) {
    const hit = FAKE_TXN_DETAILS[posOrderId];
    if (hit) return hit;
    // Unknown id in fake mode: synthesize a minimal payload so the modal still
    // renders something rather than throwing.
    return {
      id: posOrderId,
      vendor_order_id: posOrderId,
      occurred_at: new Date().toISOString(),
      location_name: 'Main Street',
      employee_name: null,
      total: 0,
      subtotal: 0,
      tax_total: 0,
      tip_total: 0,
      discount_total: 0,
      line_items: [],
      payment: { method: 'Other', last4: null },
    };
  }
  const raw = await api<Record<string, unknown>>(
    `/api/admin/locations/${encodeURIComponent(bespokeLocationId)}/square-transactions/${encodeURIComponent(posOrderId)}`,
  );
  // DECIMAL columns arrive as strings from MySQL — coerce at the edge.
  const toNum = (v: unknown): number =>
    typeof v === 'string' ? parseFloat(v) : Number(v ?? 0);
  const rawItems = Array.isArray(raw.line_items) ? (raw.line_items as Array<Record<string, unknown>>) : [];
  const rawPayment = (raw.payment ?? {}) as Record<string, unknown>;
  return {
    id: String(raw.id ?? ''),
    vendor_order_id: String(raw.vendor_order_id ?? ''),
    occurred_at: String(raw.occurred_at ?? ''),
    location_name: String(raw.location_name ?? ''),
    employee_name: (raw.employee_name as string | null) ?? null,
    total: toNum(raw.total),
    subtotal: toNum(raw.subtotal),
    tax_total: toNum(raw.tax_total),
    tip_total: toNum(raw.tip_total),
    discount_total: toNum(raw.discount_total),
    line_items: rawItems.map((it) => ({
      name: String(it.name ?? ''),
      quantity: toNum(it.quantity),
      unit_price: toNum(it.unit_price),
      line_total: toNum(it.line_total),
    })),
    payment: {
      method: ((rawPayment.method as PaymentMethod) ?? 'Other'),
      last4: (rawPayment.last4 as string | null) ?? null,
    },
  };
}
